import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Shield, LogOut, FileText, Users, Mail, MessageSquare, 
    BarChart3, Eye, Trash2, Search, MoreVertical, AlertTriangle,
    Building2, RefreshCw, Send, Megaphone, Plus, ChevronRight, ArrowLeft,
    DollarSign, UserCheck, Percent, Palette, FolderTree
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
    getAdminStats, adminGetListings, adminGetAdvertisers, adminGetUsers, adminGetContacts,
    adminDeleteListing, adminDeleteAdvertiser, adminDeleteUser, adminDeleteContact,
    adminUpdateListing, adminUpdateAdvertiser,
    adminGetMessages, adminGetConversation, adminSendMessage, adminReplyMessage,
    adminGetAnnouncements, adminCreateAnnouncement, adminDeleteAnnouncement, adminUpdateAnnouncement,
    adminGetCampaigns, adminCreateCampaign, adminDeleteCampaign,
    adminGetSalesReps, adminCreateSalesRep, adminUpdateSalesRep, adminDeleteSalesRep, adminAssignSalesRep, adminGetCommissionSummary,
    adminGetCategoryColors, adminGetCategories, adminCreateCategory, adminUpdateCategory, adminDeleteCategory,
    adminAddSubcategory, adminUpdateSubcategory, adminDeleteSubcategory
} from '../lib/api';
import { formatDate, getStatusColor } from '../lib/utils';
import { toast } from 'sonner';

const AdminDashboardPage = () => {
    const navigate = useNavigate();
    const [admin, setAdmin] = useState(null);
    const [stats, setStats] = useState(null);
    const [listings, setListings] = useState([]);
    const [advertisers, setAdvertisers] = useState([]);
    const [users, setUsers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [messages, setMessages] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [salesReps, setSalesReps] = useState([]);
    const [commissionSummary, setCommissionSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Search & filters
    const [listingSearch, setListingSearch] = useState('');
    const [listingStatus, setListingStatus] = useState('all');
    const [advertiserSearch, setAdvertiserSearch] = useState('');
    
    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', item: null });
    const [deleting, setDeleting] = useState(false);
    
    // Messaging state
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [newMessageDialog, setNewMessageDialog] = useState(false);
    const [newMessage, setNewMessage] = useState({ advertiser_id: '', subject: '', content: '' });
    const [replyContent, setReplyContent] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    
    // Announcements state
    const [newAnnouncementDialog, setNewAnnouncementDialog] = useState(false);
    const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', priority: 'normal' });
    
    // Email Campaigns state
    const [newCampaignDialog, setNewCampaignDialog] = useState(false);
    const [newCampaign, setNewCampaign] = useState({ subject: '', content: '', target_audience: 'all' });
    const [sendingCampaign, setSendingCampaign] = useState(false);
    
    // Sales Team state
    const [newSalesRepDialog, setNewSalesRepDialog] = useState(false);
    const [newSalesRep, setNewSalesRep] = useState({ name: '', email: '', phone: '', commission_rate: 10 });
    const [assignRepDialog, setAssignRepDialog] = useState({ open: false, advertiser: null });
    
    // Categories state
    const [categoriesList, setCategoriesList] = useState([]);
    const [categoryColors, setCategoryColors] = useState([]);
    const [newCategoryDialog, setNewCategoryDialog] = useState(false);
    const [newCategory, setNewCategory] = useState({ name: '', color_name: 'Slate' });
    const [editCategoryDialog, setEditCategoryDialog] = useState({ open: false, category: null });
    const [editCategory, setEditCategory] = useState({ name: '', color_name: '' });
    const [selectedCategory, setSelectedCategory] = useState(null);
    
    // Subcategory state
    const [newSubcatDialog, setNewSubcatDialog] = useState({ open: false, category: null });
    const [newSubcat, setNewSubcat] = useState({ name: '' });
    const [editSubcatDialog, setEditSubcatDialog] = useState({ open: false, category: null, subcat: null });
    const [editSubcat, setEditSubcat] = useState({ name: '' });

    useEffect(() => {
        const token = localStorage.getItem('hmc_admin_token');
        const adminData = localStorage.getItem('hmc_admin');
        
        if (!token || !adminData) {
            navigate('/admin');
            return;
        }
        
        setAdmin(JSON.parse(adminData));
        fetchAllData();
    }, [navigate]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [statsRes, listingsRes, advertisersRes, usersRes, contactsRes, messagesRes, announcementsRes, campaignsRes, salesRepsRes, commissionRes, categoriesRes, colorsRes] = await Promise.all([
                getAdminStats(),
                adminGetListings({}),
                adminGetAdvertisers({}),
                adminGetUsers({}),
                adminGetContacts({}),
                adminGetMessages(),
                adminGetAnnouncements(),
                adminGetCampaigns().catch(() => ({ data: [] })),
                adminGetSalesReps().catch(() => ({ data: [] })),
                adminGetCommissionSummary().catch(() => ({ data: null })),
                adminGetCategories().catch(() => ({ data: [] })),
                adminGetCategoryColors().catch(() => ({ data: [] }))
            ]);
            
            setStats(statsRes.data);
            setListings(listingsRes.data.listings);
            setAdvertisers(advertisersRes.data.advertisers);
            setUsers(usersRes.data.users);
            setContacts(contactsRes.data.messages);
            setMessages(messagesRes.data);
            setAnnouncements(announcementsRes.data);
            setCampaigns(campaignsRes.data);
            setSalesReps(salesRepsRes.data);
            setCommissionSummary(commissionRes.data);
            setCategoriesList(categoriesRes.data);
            setCategoryColors(colorsRes.data);
        } catch (error) {
            console.error('Error fetching admin data:', error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('hmc_admin_token');
                localStorage.removeItem('hmc_admin');
                navigate('/admin');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('hmc_admin_token');
        localStorage.removeItem('hmc_admin');
        navigate('/admin');
    };

    const openDeleteDialog = (type, item) => {
        setDeleteDialog({ open: true, type, item });
    };

    const handleDelete = async () => {
        const { type, item } = deleteDialog;
        setDeleting(true);
        
        try {
            switch (type) {
                case 'listing':
                    await adminDeleteListing(item.id);
                    setListings(listings.filter(l => l.id !== item.id));
                    break;
                case 'advertiser':
                    await adminDeleteAdvertiser(item.id);
                    setAdvertisers(advertisers.filter(a => a.id !== item.id));
                    break;
                case 'user':
                    await adminDeleteUser(item.email);
                    setUsers(users.filter(u => u.email !== item.email));
                    break;
                case 'contact':
                    await adminDeleteContact(item.id);
                    setContacts(contacts.filter(c => c.id !== item.id));
                    break;
                case 'announcement':
                    await adminDeleteAnnouncement(item.id);
                    setAnnouncements(announcements.filter(a => a.id !== item.id));
                    break;
                case 'campaign':
                    await adminDeleteCampaign(item.id);
                    setCampaigns(campaigns.filter(c => c.id !== item.id));
                    break;
                case 'salesRep':
                    await adminDeleteSalesRep(item.id);
                    setSalesReps(salesReps.filter(r => r.id !== item.id));
                    break;
                case 'category':
                    await adminDeleteCategory(item.slug);
                    const catRes = await adminGetCategories();
                    setCategoriesList(catRes.data);
                    break;
            }
            toast.success('Deleted successfully');
            setDeleteDialog({ open: false, type: '', item: null });
        } catch (error) {
            toast.error('Failed to delete');
        } finally {
            setDeleting(false);
        }
    };

    const handleStatusChange = async (listingId, newStatus) => {
        try {
            await adminUpdateListing(listingId, { status: newStatus });
            setListings(listings.map(l => l.id === listingId ? { ...l, status: newStatus } : l));
            toast.success('Status updated');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleAdvertiserStatusChange = async (advertiserId, isActive) => {
        try {
            await adminUpdateAdvertiser(advertiserId, { is_active: isActive });
            setAdvertisers(advertisers.map(a => a.id === advertiserId ? { ...a, is_active: isActive } : a));
            toast.success(`Advertiser ${isActive ? 'activated' : 'deactivated'}`);
        } catch (error) {
            toast.error('Failed to update advertiser');
        }
    };

    // Messaging functions
    const openConversation = async (conversationId) => {
        try {
            const res = await adminGetConversation(conversationId);
            setSelectedConversation(res.data);
            setConversationMessages(res.data.messages);
        } catch (error) {
            toast.error('Failed to load conversation');
        }
    };

    const handleSendNewMessage = async () => {
        if (!newMessage.advertiser_id || !newMessage.subject || !newMessage.content) {
            toast.error('Please fill all fields');
            return;
        }
        
        setSendingMessage(true);
        try {
            await adminSendMessage(newMessage);
            toast.success('Message sent successfully');
            setNewMessageDialog(false);
            setNewMessage({ advertiser_id: '', subject: '', content: '' });
            // Refresh messages
            const res = await adminGetMessages();
            setMessages(res.data);
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleReply = async () => {
        if (!replyContent.trim()) return;
        
        setSendingMessage(true);
        try {
            await adminReplyMessage(selectedConversation.conversation_id, { content: replyContent });
            toast.success('Reply sent');
            setReplyContent('');
            // Refresh conversation
            openConversation(selectedConversation.conversation_id);
            // Refresh messages list
            const res = await adminGetMessages();
            setMessages(res.data);
        } catch (error) {
            toast.error('Failed to send reply');
        } finally {
            setSendingMessage(false);
        }
    };

    // Announcement functions
    const handleCreateAnnouncement = async () => {
        if (!newAnnouncement.title || !newAnnouncement.content) {
            toast.error('Please fill all fields');
            return;
        }
        
        try {
            await adminCreateAnnouncement(newAnnouncement);
            toast.success('Announcement created');
            setNewAnnouncementDialog(false);
            setNewAnnouncement({ title: '', content: '', priority: 'normal' });
            const res = await adminGetAnnouncements();
            setAnnouncements(res.data);
        } catch (error) {
            toast.error('Failed to create announcement');
        }
    };

    const toggleAnnouncementActive = async (announcement) => {
        try {
            await adminUpdateAnnouncement(announcement.id, { is_active: !announcement.is_active });
            setAnnouncements(announcements.map(a => 
                a.id === announcement.id ? { ...a, is_active: !a.is_active } : a
            ));
            toast.success(announcement.is_active ? 'Announcement hidden' : 'Announcement published');
        } catch (error) {
            toast.error('Failed to update announcement');
        }
    };

    // Email Campaign functions
    const handleCreateCampaign = async () => {
        if (!newCampaign.subject || !newCampaign.content) {
            toast.error('Please fill all fields');
            return;
        }
        
        setSendingCampaign(true);
        try {
            const res = await adminCreateCampaign(newCampaign);
            toast.success(`Campaign sent (MOCKED) to ${res.data.recipient_count} recipients`);
            setNewCampaignDialog(false);
            setNewCampaign({ subject: '', content: '', target_audience: 'all' });
            const campaignsRes = await adminGetCampaigns();
            setCampaigns(campaignsRes.data);
        } catch (error) {
            toast.error('Failed to create campaign');
        } finally {
            setSendingCampaign(false);
        }
    };

    // Sales Team functions
    const handleCreateSalesRep = async () => {
        if (!newSalesRep.name || !newSalesRep.email) {
            toast.error('Please fill name and email');
            return;
        }
        
        try {
            await adminCreateSalesRep(newSalesRep);
            toast.success('Sales rep added');
            setNewSalesRepDialog(false);
            setNewSalesRep({ name: '', email: '', phone: '', commission_rate: 10 });
            const [repsRes, summaryRes] = await Promise.all([
                adminGetSalesReps(),
                adminGetCommissionSummary()
            ]);
            setSalesReps(repsRes.data);
            setCommissionSummary(summaryRes.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create sales rep');
        }
    };

    const handleAssignSalesRep = async (salesRepId) => {
        if (!assignRepDialog.advertiser) return;
        
        try {
            await adminAssignSalesRep(assignRepDialog.advertiser.id, salesRepId || null);
            toast.success(salesRepId ? 'Sales rep assigned' : 'Sales rep unassigned');
            setAssignRepDialog({ open: false, advertiser: null });
            // Refresh data
            const [advertisersRes, repsRes, summaryRes] = await Promise.all([
                adminGetAdvertisers({}),
                adminGetSalesReps(),
                adminGetCommissionSummary()
            ]);
            setAdvertisers(advertisersRes.data.advertisers);
            setSalesReps(repsRes.data);
            setCommissionSummary(summaryRes.data);
        } catch (error) {
            toast.error('Failed to assign sales rep');
        }
    };

    const toggleSalesRepActive = async (rep) => {
        try {
            await adminUpdateSalesRep(rep.id, { is_active: !rep.is_active });
            setSalesReps(salesReps.map(r => 
                r.id === rep.id ? { ...r, is_active: !r.is_active } : r
            ));
            toast.success(rep.is_active ? 'Sales rep deactivated' : 'Sales rep activated');
        } catch (error) {
            toast.error('Failed to update sales rep');
        }
    };

    const getSalesRepName = (salesRepId) => {
        const rep = salesReps.find(r => r.id === salesRepId);
        return rep ? rep.name : null;
    };

    // Category functions
    const handleCreateCategory = async () => {
        if (!newCategory.name) {
            toast.error('Please enter a category name');
            return;
        }
        
        try {
            await adminCreateCategory(newCategory);
            toast.success('Category created');
            setNewCategoryDialog(false);
            setNewCategory({ name: '', color_name: 'Slate' });
            const catRes = await adminGetCategories();
            setCategoriesList(catRes.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create category');
        }
    };

    const handleUpdateCategory = async () => {
        if (!editCategoryDialog.category) return;
        
        try {
            await adminUpdateCategory(editCategoryDialog.category.slug, editCategory);
            toast.success('Category updated');
            setEditCategoryDialog({ open: false, category: null });
            const catRes = await adminGetCategories();
            setCategoriesList(catRes.data);
        } catch (error) {
            toast.error('Failed to update category');
        }
    };

    const toggleCategoryActive = async (category) => {
        try {
            await adminUpdateCategory(category.slug, { is_active: !category.is_active });
            setCategoriesList(categoriesList.map(c => 
                c.slug === category.slug ? { ...c, is_active: !c.is_active } : c
            ));
            toast.success(category.is_active ? 'Category hidden' : 'Category activated');
        } catch (error) {
            toast.error('Failed to update category');
        }
    };

    const openEditCategory = (category) => {
        // Find the color name from the color class
        const colorOption = categoryColors.find(c => c.color === category.color);
        setEditCategory({
            name: category.name,
            color_name: colorOption?.name || 'Slate'
        });
        setEditCategoryDialog({ open: true, category });
    };

    // Subcategory functions
    const handleAddSubcategory = async () => {
        if (!newSubcat.name || !newSubcatDialog.category) {
            toast.error('Please enter a subcategory name');
            return;
        }
        
        try {
            await adminAddSubcategory(newSubcatDialog.category.slug, newSubcat);
            toast.success('Subcategory added');
            setNewSubcatDialog({ open: false, category: null });
            setNewSubcat({ name: '' });
            const catRes = await adminGetCategories();
            setCategoriesList(catRes.data);
            // Update selected category if viewing
            if (selectedCategory?.slug === newSubcatDialog.category.slug) {
                const updated = catRes.data.find(c => c.slug === selectedCategory.slug);
                setSelectedCategory(updated);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add subcategory');
        }
    };

    const handleUpdateSubcategory = async () => {
        if (!editSubcat.name || !editSubcatDialog.category || !editSubcatDialog.subcat) {
            toast.error('Please enter a subcategory name');
            return;
        }
        
        try {
            await adminUpdateSubcategory(
                editSubcatDialog.category.slug, 
                editSubcatDialog.subcat.slug, 
                editSubcat
            );
            toast.success('Subcategory updated');
            setEditSubcatDialog({ open: false, category: null, subcat: null });
            setEditSubcat({ name: '' });
            const catRes = await adminGetCategories();
            setCategoriesList(catRes.data);
            // Update selected category if viewing
            if (selectedCategory?.slug === editSubcatDialog.category.slug) {
                const updated = catRes.data.find(c => c.slug === selectedCategory.slug);
                setSelectedCategory(updated);
            }
        } catch (error) {
            toast.error('Failed to update subcategory');
        }
    };

    const handleDeleteSubcategory = async (category, subcat) => {
        try {
            const res = await adminDeleteSubcategory(category.slug, subcat.slug);
            toast.success(res.data.message);
            const catRes = await adminGetCategories();
            setCategoriesList(catRes.data);
            // Update selected category if viewing
            if (selectedCategory?.slug === category.slug) {
                const updated = catRes.data.find(c => c.slug === category.slug);
                setSelectedCategory(updated);
            }
        } catch (error) {
            toast.error('Failed to delete subcategory');
        }
    };

    const filteredListings = listings.filter(l => {
        const matchesSearch = !listingSearch || 
            l.title.toLowerCase().includes(listingSearch.toLowerCase()) ||
            l.business_name?.toLowerCase().includes(listingSearch.toLowerCase());
        const matchesStatus = listingStatus === 'all' || l.status === listingStatus;
        return matchesSearch && matchesStatus;
    });

    const filteredAdvertisers = advertisers.filter(a => {
        return !advertiserSearch || 
            a.business_name?.toLowerCase().includes(advertiserSearch.toLowerCase()) ||
            a.email?.toLowerCase().includes(advertiserSearch.toLowerCase()) ||
            a.first_name?.toLowerCase().includes(advertiserSearch.toLowerCase());
    });

    const totalUnreadMessages = messages.reduce((sum, m) => sum + (m.unread_count || 0), 0);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="spinner border-amber-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
            {/* Header */}
            <header className="bg-slate-900 text-white sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-3">
                            <Shield className="w-8 h-8 text-amber-500" />
                            <div>
                                <h1 className="font-bold text-lg">HMC Admin</h1>
                                <p className="text-xs text-gray-400">Super Admin Panel</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-300">{admin?.email}</span>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={handleLogout}
                                className="border-gray-600 text-gray-300 hover:bg-gray-800"
                                data-testid="admin-logout-btn"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                <FileText className="w-4 h-4" /> Listings
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.total_listings || 0}</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                <Building2 className="w-4 h-4" /> Advertisers
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.total_advertisers || 0}</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                <Users className="w-4 h-4" /> Users
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" /> Contact
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{stats?.total_contacts || 0}</div>
                        </CardContent>
                    </Card>
                    <Card className={totalUnreadMessages > 0 ? 'border-amber-500' : ''}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                <Mail className="w-4 h-4" /> Messages
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{messages.length}</div>
                            {totalUnreadMessages > 0 && (
                                <p className="text-xs text-amber-600">{totalUnreadMessages} unread</p>
                            )}
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                <Megaphone className="w-4 h-4" /> Announcements
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{announcements.filter(a => a.is_active).length}</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <div className="flex justify-between items-center flex-wrap gap-2">
                        <TabsList>
                            <TabsTrigger value="overview">Overview</TabsTrigger>
                            <TabsTrigger value="listings">Listings</TabsTrigger>
                            <TabsTrigger value="advertisers">Advertisers</TabsTrigger>
                            <TabsTrigger value="categories">Categories</TabsTrigger>
                            <TabsTrigger value="messages" className="relative">
                                Messages
                                {totalUnreadMessages > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                                        {totalUnreadMessages}
                                    </span>
                                )}
                            </TabsTrigger>
                            <TabsTrigger value="announcements">Announcements</TabsTrigger>
                            <TabsTrigger value="campaigns">Email Campaigns</TabsTrigger>
                            <TabsTrigger value="sales">Sales Team</TabsTrigger>
                            <TabsTrigger value="users">Users</TabsTrigger>
                            <TabsTrigger value="contacts">Contact Msgs</TabsTrigger>
                        </TabsList>
                        <Button variant="outline" size="sm" onClick={fetchAllData}>
                            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                        </Button>
                    </div>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Recent Listings</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {listings.slice(0, 5).map(listing => (
                                            <div key={listing.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                                <div>
                                                    <p className="font-medium text-sm">{listing.title}</p>
                                                    <p className="text-xs text-gray-500">{listing.business_name}</p>
                                                </div>
                                                <Badge className={getStatusColor(listing.status)}>{listing.status}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle>Recent Messages</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {messages.slice(0, 5).map(msg => (
                                            <div 
                                                key={msg.conversation_id} 
                                                className={`p-2 rounded cursor-pointer hover:bg-gray-100 ${msg.unread_count > 0 ? 'bg-amber-50 border-l-4 border-amber-500' : 'bg-gray-50'}`}
                                                onClick={() => { setActiveTab('messages'); openConversation(msg.conversation_id); }}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <p className="font-medium text-sm">{msg.advertiser_name}</p>
                                                    {msg.unread_count > 0 && (
                                                        <Badge className="bg-amber-500">{msg.unread_count} new</Badge>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-600 truncate">{msg.subject}</p>
                                            </div>
                                        ))}
                                        {messages.length === 0 && (
                                            <p className="text-sm text-gray-500 text-center py-4">No messages yet</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Listings Tab */}
                    <TabsContent value="listings">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row justify-between gap-4">
                                    <CardTitle>All Listings</CardTitle>
                                    <div className="flex gap-2">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <Input
                                                placeholder="Search..."
                                                value={listingSearch}
                                                onChange={(e) => setListingSearch(e.target.value)}
                                                className="pl-9 w-48"
                                            />
                                        </div>
                                        <Select value={listingStatus} onValueChange={setListingStatus}>
                                            <SelectTrigger className="w-32">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Status</SelectItem>
                                                <SelectItem value="active">Active</SelectItem>
                                                <SelectItem value="on_hold">On Hold</SelectItem>
                                                <SelectItem value="deleted">Deleted</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Title</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Advertiser</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Category</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Views</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {filteredListings.map(listing => (
                                                <tr key={listing.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 font-medium">{listing.title}</td>
                                                    <td className="px-4 py-3 text-sm">{listing.business_name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm">{listing.category_name}</td>
                                                    <td className="px-4 py-3">
                                                        <Badge className={getStatusColor(listing.status)}>{listing.status}</Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">{listing.views}</td>
                                                    <td className="px-4 py-3">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => handleStatusChange(listing.id, 'active')}>Set Active</DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleStatusChange(listing.id, 'on_hold')}>Put on Hold</DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => openDeleteDialog('listing', listing)} className="text-red-600">
                                                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Advertisers Tab */}
                    <TabsContent value="advertisers">
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <CardTitle>All Advertisers</CardTitle>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <Input
                                            placeholder="Search..."
                                            value={advertiserSearch}
                                            onChange={(e) => setAdvertiserSearch(e.target.value)}
                                            className="pl-9 w-64"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Business</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Contact</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Listings</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {filteredAdvertisers.map(adv => (
                                                <tr key={adv.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <p className="font-medium">{adv.business_name}</p>
                                                        <p className="text-xs text-gray-500">{adv.first_name} {adv.last_name}</p>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <p>{adv.email}</p>
                                                        <p className="text-xs text-gray-500">{adv.phone}</p>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">{adv.listing_count}</td>
                                                    <td className="px-4 py-3">
                                                        <Badge className={adv.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                                            {adv.is_active !== false ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => {
                                                                    setNewMessage({ advertiser_id: adv.id, subject: '', content: '' });
                                                                    setNewMessageDialog(true);
                                                                }}>
                                                                    <Mail className="w-4 h-4 mr-2" /> Send Message
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleAdvertiserStatusChange(adv.id, !adv.is_active)}>
                                                                    {adv.is_active !== false ? 'Deactivate' : 'Activate'}
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => openDeleteDialog('advertiser', adv)} className="text-red-600">
                                                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Categories Tab */}
                    <TabsContent value="categories">
                        <div className="grid lg:grid-cols-3 gap-6">
                            {/* Categories List */}
                            <div className="lg:col-span-2">
                                <Card>
                                    <CardHeader>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <CardTitle className="flex items-center gap-2">
                                                    <FolderTree className="w-5 h-5" />
                                                    Manage Categories
                                                </CardTitle>
                                                <p className="text-sm text-gray-500 mt-1">Click a category to manage its subcategories</p>
                                            </div>
                                            <Button onClick={() => setNewCategoryDialog(true)} data-testid="new-category-btn">
                                                <Plus className="w-4 h-4 mr-2" /> Add Category
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            {categoriesList.map(cat => (
                                                <div 
                                                    key={cat.slug}
                                                    className={`rounded-xl p-4 border-2 cursor-pointer transition-all ${cat.color || 'bg-gray-100 border-gray-300'} ${!cat.is_active ? 'opacity-50' : ''} ${selectedCategory?.slug === cat.slug ? 'ring-2 ring-navy ring-offset-2' : ''}`}
                                                    data-testid={`category-card-${cat.slug}`}
                                                    onClick={() => setSelectedCategory(cat)}
                                                >
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div>
                                                            <h3 className="font-semibold text-gray-800">{cat.name}</h3>
                                                            <p className="text-xs text-gray-500">{cat.slug}</p>
                                                        </div>
                                                        <Badge variant={cat.is_active ? 'default' : 'secondary'}>
                                                            {cat.is_active ? 'Active' : 'Hidden'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-sm text-gray-600 mb-3">
                                                        {cat.subcategories?.length || 0} subcategories
                                                    </p>
                                                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                        <Button size="sm" variant="outline" onClick={() => openEditCategory(cat)}>
                                                            <Palette className="w-3 h-3 mr-1" /> Edit
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => toggleCategoryActive(cat)}>
                                                            {cat.is_active ? 'Hide' : 'Show'}
                                                        </Button>
                                                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => openDeleteDialog('category', cat)}>
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {categoriesList.length === 0 && (
                                            <div className="text-center py-8">
                                                <FolderTree className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                                <p className="text-gray-500">No categories found</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Subcategories Panel */}
                            <div>
                                <Card className="sticky top-20">
                                    <CardHeader>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <CardTitle className="text-lg">
                                                    {selectedCategory ? `${selectedCategory.name} Subcategories` : 'Subcategories'}
                                                </CardTitle>
                                                {selectedCategory && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {selectedCategory.subcategories?.length || 0} items
                                                    </p>
                                                )}
                                            </div>
                                            {selectedCategory && (
                                                <Button 
                                                    size="sm" 
                                                    onClick={() => setNewSubcatDialog({ open: true, category: selectedCategory })}
                                                    data-testid="add-subcategory-btn"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {selectedCategory ? (
                                            selectedCategory.subcategories?.length > 0 ? (
                                                <div className="space-y-2">
                                                    {selectedCategory.subcategories.map(subcat => (
                                                        <div 
                                                            key={subcat.slug}
                                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                                                            data-testid={`subcat-${subcat.slug}`}
                                                        >
                                                            <div>
                                                                <p className="font-medium text-sm">{subcat.name}</p>
                                                                <p className="text-xs text-gray-500">{subcat.slug}</p>
                                                            </div>
                                                            <div className="flex gap-1">
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="ghost"
                                                                    onClick={() => {
                                                                        setEditSubcat({ name: subcat.name });
                                                                        setEditSubcatDialog({ open: true, category: selectedCategory, subcat });
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="ghost" 
                                                                    className="text-red-600"
                                                                    onClick={() => handleDeleteSubcategory(selectedCategory, subcat)}
                                                                >
                                                                    <Trash2 className="w-3 h-3" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-6">
                                                    <p className="text-gray-500 text-sm mb-3">No subcategories</p>
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        onClick={() => setNewSubcatDialog({ open: true, category: selectedCategory })}
                                                    >
                                                        <Plus className="w-4 h-4 mr-1" /> Add First Subcategory
                                                    </Button>
                                                </div>
                                            )
                                        ) : (
                                            <div className="text-center py-8 text-gray-400">
                                                <FolderTree className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">Select a category to manage subcategories</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                {/* Color Palette Reference */}
                                <Card className="mt-4">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Color Palette</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="grid grid-cols-4 gap-2">
                                            {categoryColors.map(color => (
                                                <div 
                                                    key={color.name}
                                                    className={`rounded p-2 text-center ${color.color}`}
                                                    title={color.name}
                                                >
                                                    <span className={`text-xs font-medium ${color.icon_color}`}>{color.name.slice(0, 3)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Messages Tab */}
                    <TabsContent value="messages">
                        <div className="grid md:grid-cols-3 gap-4">
                            {/* Conversations List */}
                            <Card className="md:col-span-1">
                                <CardHeader className="pb-2">
                                    <div className="flex justify-between items-center">
                                        <CardTitle className="text-lg">Conversations</CardTitle>
                                        <Button size="sm" onClick={() => setNewMessageDialog(true)} data-testid="new-message-btn">
                                            <Plus className="w-4 h-4 mr-1" /> New
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="divide-y max-h-[500px] overflow-y-auto">
                                        {messages.map(msg => (
                                            <div 
                                                key={msg.conversation_id}
                                                className={`p-3 cursor-pointer hover:bg-gray-50 ${selectedConversation?.conversation_id === msg.conversation_id ? 'bg-blue-50' : ''} ${msg.unread_count > 0 ? 'border-l-4 border-amber-500' : ''}`}
                                                onClick={() => openConversation(msg.conversation_id)}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <p className="font-medium text-sm">{msg.advertiser_name}</p>
                                                    {msg.unread_count > 0 && (
                                                        <Badge className="bg-amber-500 text-xs">{msg.unread_count}</Badge>
                                                    )}
                                                </div>
                                                <p className="text-sm font-medium text-gray-700 truncate">{msg.subject}</p>
                                                <p className="text-xs text-gray-500 truncate">{msg.last_message}</p>
                                                <p className="text-xs text-gray-400 mt-1">{formatDate(msg.last_date)}</p>
                                            </div>
                                        ))}
                                        {messages.length === 0 && (
                                            <p className="text-sm text-gray-500 text-center py-8">No conversations yet</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Conversation Detail */}
                            <Card className="md:col-span-2">
                                <CardHeader className="pb-2 border-b">
                                    {selectedConversation ? (
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle className="text-lg">{selectedConversation.advertiser?.business_name}</CardTitle>
                                                <p className="text-sm text-gray-500">{conversationMessages[0]?.subject}</p>
                                            </div>
                                            <Button variant="ghost" size="sm" onClick={() => setSelectedConversation(null)}>
                                                <ArrowLeft className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <CardTitle className="text-lg text-gray-400">Select a conversation</CardTitle>
                                    )}
                                </CardHeader>
                                <CardContent className="p-0">
                                    {selectedConversation ? (
                                        <>
                                            <div className="h-[350px] overflow-y-auto p-4 space-y-3">
                                                {conversationMessages.map(msg => (
                                                    <div 
                                                        key={msg.id}
                                                        className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                                                    >
                                                        <div className={`max-w-[80%] rounded-lg p-3 ${msg.sender_type === 'admin' ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}>
                                                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                            <p className={`text-xs mt-1 ${msg.sender_type === 'admin' ? 'text-blue-100' : 'text-gray-500'}`}>
                                                                {msg.sender_name} • {formatDate(msg.created_at)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="p-4 border-t">
                                                <div className="flex gap-2">
                                                    <Textarea
                                                        placeholder="Type your reply..."
                                                        value={replyContent}
                                                        onChange={(e) => setReplyContent(e.target.value)}
                                                        rows={2}
                                                        className="flex-1"
                                                    />
                                                    <Button onClick={handleReply} disabled={sendingMessage || !replyContent.trim()}>
                                                        <Send className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="h-[400px] flex items-center justify-center text-gray-400">
                                            <p>Select a conversation to view messages</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Announcements Tab */}
                    <TabsContent value="announcements">
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <CardTitle>Announcements</CardTitle>
                                    <Button onClick={() => setNewAnnouncementDialog(true)} data-testid="new-announcement-btn">
                                        <Plus className="w-4 h-4 mr-2" /> New Announcement
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {announcements.map(ann => (
                                        <div key={ann.id} className={`border rounded-lg p-4 ${!ann.is_active ? 'opacity-50' : ''}`}>
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-semibold">{ann.title}</h3>
                                                    <Badge variant={ann.priority === 'urgent' ? 'destructive' : ann.priority === 'important' ? 'default' : 'secondary'}>
                                                        {ann.priority}
                                                    </Badge>
                                                    {!ann.is_active && <Badge variant="outline">Hidden</Badge>}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button variant="ghost" size="sm" onClick={() => toggleAnnouncementActive(ann)}>
                                                        {ann.is_active ? 'Hide' : 'Publish'}
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openDeleteDialog('announcement', ann)} className="text-red-600">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <p className="text-gray-700 whitespace-pre-wrap">{ann.content}</p>
                                            <p className="text-xs text-gray-400 mt-2">{formatDate(ann.created_at)} by {ann.created_by_name}</p>
                                        </div>
                                    ))}
                                    {announcements.length === 0 && (
                                        <p className="text-center text-gray-500 py-8">No announcements yet</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Email Campaigns Tab */}
                    <TabsContent value="campaigns">
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <CardTitle>Email Marketing Campaigns</CardTitle>
                                        <p className="text-sm text-gray-500 mt-1">Send email campaigns to your users and advertisers (MOCKED - no actual emails sent)</p>
                                    </div>
                                    <Button onClick={() => setNewCampaignDialog(true)} data-testid="new-campaign-btn">
                                        <Plus className="w-4 h-4 mr-2" /> New Campaign
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {campaigns.map(campaign => (
                                        <div key={campaign.id} className="border rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="font-semibold">{campaign.subject}</h3>
                                                        <Badge variant="secondary" className="capitalize">{campaign.target_audience}</Badge>
                                                        <Badge className="bg-green-100 text-green-800">Sent (MOCKED)</Badge>
                                                    </div>
                                                    <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">{campaign.content}</p>
                                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                                        <span>Recipients: {campaign.recipient_count}</span>
                                                        <span>Sent: {formatDate(campaign.sent_at)}</span>
                                                        <span>By: {campaign.created_by_name}</span>
                                                    </div>
                                                </div>
                                                <Button variant="ghost" size="sm" onClick={() => openDeleteDialog('campaign', campaign)} className="text-red-600">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    {campaigns.length === 0 && (
                                        <div className="text-center py-8">
                                            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500">No campaigns yet</p>
                                            <p className="text-sm text-gray-400">Create your first email campaign to reach your audience</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Sales Team Tab */}
                    <TabsContent value="sales">
                        {/* Commission Summary */}
                        {commissionSummary && (
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                            <Users className="w-4 h-4" /> Active Reps
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{commissionSummary.total_sales_reps}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                            <DollarSign className="w-4 h-4" /> Revenue from Sales
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-green-600">${commissionSummary.total_revenue_from_sales}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                            <DollarSign className="w-4 h-4" /> Commission Owed
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold text-amber-600">${commissionSummary.total_commission_owed.toFixed(2)}</div>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                                            <Percent className="w-4 h-4" /> Avg Commission Rate
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-2xl font-bold">{commissionSummary.average_commission_rate.toFixed(1)}%</div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                        
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <CardTitle>Sales Team Members</CardTitle>
                                        <p className="text-sm text-gray-500 mt-1">Manage your sales representatives and track their commissions</p>
                                    </div>
                                    <Button onClick={() => setNewSalesRepDialog(true)} data-testid="new-sales-rep-btn">
                                        <Plus className="w-4 h-4 mr-2" /> Add Sales Rep
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Name</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Email</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Phone</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Advertisers</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Paid</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Revenue</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Rate</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Commission</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {salesReps.map(rep => (
                                                <tr key={rep.id} className="hover:bg-gray-50" data-testid={`sales-rep-${rep.id}`}>
                                                    <td className="px-4 py-3 font-medium">{rep.name}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{rep.email}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{rep.phone || '-'}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <Badge variant="outline">{rep.advertiser_count}</Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <Badge className="bg-green-100 text-green-800">{rep.paid_advertiser_count}</Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-green-600">${rep.total_revenue}</td>
                                                    <td className="px-4 py-3 text-sm">{rep.commission_rate}%</td>
                                                    <td className="px-4 py-3 text-sm font-medium text-amber-600">${rep.commission_earned.toFixed(2)}</td>
                                                    <td className="px-4 py-3">
                                                        <Badge className={rep.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                                                            {rep.is_active ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => toggleSalesRepActive(rep)}>
                                                                    {rep.is_active ? 'Deactivate' : 'Activate'}
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem 
                                                                    onClick={() => openDeleteDialog('salesRep', rep)}
                                                                    className="text-red-600"
                                                                >
                                                                    Delete
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {salesReps.length === 0 && (
                                        <div className="text-center py-8">
                                            <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500">No sales reps yet</p>
                                            <p className="text-sm text-gray-400">Add your first sales team member to start tracking commissions</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Advertisers by Sales Rep */}
                        <Card className="mt-6">
                            <CardHeader>
                                <CardTitle>Assign Advertisers to Sales Reps</CardTitle>
                                <p className="text-sm text-gray-500">Track which sales rep brought in each advertiser</p>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Business</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Contact</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Membership</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Assigned Rep</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {advertisers.map(adv => (
                                                <tr key={adv.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 font-medium">{adv.business_name}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{adv.email}</td>
                                                    <td className="px-4 py-3">
                                                        <Badge className={adv.membership_type === 'free' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-800'}>
                                                            {adv.membership_type}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        {getSalesRepName(adv.sales_rep_id) || <span className="text-gray-400">Unassigned</span>}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <Button 
                                                            variant="outline" 
                                                            size="sm"
                                                            onClick={() => setAssignRepDialog({ open: true, advertiser: adv })}
                                                        >
                                                            {adv.sales_rep_id ? 'Change' : 'Assign'}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Users Tab */}
                    <TabsContent value="users">
                        <Card>
                            <CardHeader><CardTitle>Collected User Emails</CardTitle></CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Email</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Signed Up</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                            {users.map(user => (
                                                <tr key={user.email} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 font-medium">{user.email}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(user.created_at)}</td>
                                                    <td className="px-4 py-3">
                                                        <Button variant="ghost" size="sm" onClick={() => openDeleteDialog('user', user)} className="text-red-600">
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Contacts Tab */}
                    <TabsContent value="contacts">
                        <Card>
                            <CardHeader><CardTitle>Contact Messages</CardTitle></CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {contacts.map(msg => (
                                        <div key={msg.id} className="border rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <p className="font-semibold">{msg.subject}</p>
                                                    <p className="text-sm text-gray-500">{msg.name} &lt;{msg.email}&gt;</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                                                    <Button variant="ghost" size="sm" onClick={() => openDeleteDialog('contact', msg)} className="text-red-600">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <p className="text-gray-700 whitespace-pre-line">{msg.message}</p>
                                        </div>
                                    ))}
                                    {contacts.length === 0 && <p className="text-center text-gray-500 py-8">No contact messages</p>}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" /> Confirm Delete
                        </DialogTitle>
                        <DialogDescription>
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: '', item: null })}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Message Dialog */}
            <Dialog open={newMessageDialog} onOpenChange={setNewMessageDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Send Message to Advertiser</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Select Advertiser</Label>
                            <Select value={newMessage.advertiser_id} onValueChange={(v) => setNewMessage({ ...newMessage, advertiser_id: v })}>
                                <SelectTrigger><SelectValue placeholder="Choose advertiser" /></SelectTrigger>
                                <SelectContent>
                                    {advertisers.map(adv => (
                                        <SelectItem key={adv.id} value={adv.id}>{adv.business_name} ({adv.email})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Subject</Label>
                            <Input value={newMessage.subject} onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })} placeholder="Message subject" />
                        </div>
                        <div>
                            <Label>Message</Label>
                            <Textarea value={newMessage.content} onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })} placeholder="Type your message..." rows={4} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewMessageDialog(false)}>Cancel</Button>
                        <Button onClick={handleSendNewMessage} disabled={sendingMessage}>
                            {sendingMessage ? 'Sending...' : 'Send Message'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Announcement Dialog */}
            <Dialog open={newAnnouncementDialog} onOpenChange={setNewAnnouncementDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create Announcement</DialogTitle>
                        <DialogDescription>This will be visible to all advertisers on their dashboard.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Title</Label>
                            <Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} placeholder="Announcement title" />
                        </div>
                        <div>
                            <Label>Priority</Label>
                            <Select value={newAnnouncement.priority} onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, priority: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="normal">Normal</SelectItem>
                                    <SelectItem value="important">Important</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Content</Label>
                            <Textarea value={newAnnouncement.content} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })} placeholder="Announcement content..." rows={4} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewAnnouncementDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateAnnouncement}>Create Announcement</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Campaign Dialog */}
            <Dialog open={newCampaignDialog} onOpenChange={setNewCampaignDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create Email Campaign</DialogTitle>
                        <DialogDescription>
                            Send an email to your subscribers. (MOCKED - emails will be saved but not actually sent)
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Target Audience</Label>
                            <Select value={newCampaign.target_audience} onValueChange={(v) => setNewCampaign({ ...newCampaign, target_audience: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Subscribers ({users.length + advertisers.length} recipients)</SelectItem>
                                    <SelectItem value="advertisers">Advertisers Only ({advertisers.length} recipients)</SelectItem>
                                    <SelectItem value="users">Email Subscribers Only ({users.length} recipients)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Email Subject</Label>
                            <Input 
                                value={newCampaign.subject} 
                                onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })} 
                                placeholder="e.g., New Features on Hotel Motel Club!"
                                data-testid="campaign-subject-input"
                            />
                        </div>
                        <div>
                            <Label>Email Content</Label>
                            <Textarea 
                                value={newCampaign.content} 
                                onChange={(e) => setNewCampaign({ ...newCampaign, content: e.target.value })} 
                                placeholder="Write your email content here..." 
                                rows={6}
                                data-testid="campaign-content-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewCampaignDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateCampaign} disabled={sendingCampaign} data-testid="send-campaign-btn">
                            {sendingCampaign ? 'Sending...' : 'Send Campaign (MOCKED)'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Sales Rep Dialog */}
            <Dialog open={newSalesRepDialog} onOpenChange={setNewSalesRepDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Sales Team Member</DialogTitle>
                        <DialogDescription>
                            Add a new sales representative to track their commissions
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Full Name *</Label>
                            <Input 
                                value={newSalesRep.name} 
                                onChange={(e) => setNewSalesRep({ ...newSalesRep, name: e.target.value })} 
                                placeholder="John Smith"
                                data-testid="sales-rep-name-input"
                            />
                        </div>
                        <div>
                            <Label>Email *</Label>
                            <Input 
                                type="email"
                                value={newSalesRep.email} 
                                onChange={(e) => setNewSalesRep({ ...newSalesRep, email: e.target.value })} 
                                placeholder="john@company.com"
                                data-testid="sales-rep-email-input"
                            />
                        </div>
                        <div>
                            <Label>Phone</Label>
                            <Input 
                                value={newSalesRep.phone} 
                                onChange={(e) => setNewSalesRep({ ...newSalesRep, phone: e.target.value })} 
                                placeholder="(555) 123-4567"
                            />
                        </div>
                        <div>
                            <Label>Commission Rate (%)</Label>
                            <Input 
                                type="number"
                                min="0"
                                max="100"
                                value={newSalesRep.commission_rate} 
                                onChange={(e) => setNewSalesRep({ ...newSalesRep, commission_rate: parseFloat(e.target.value) || 0 })} 
                                placeholder="10"
                            />
                            <p className="text-xs text-gray-500 mt-1">Percentage of revenue from advertisers they bring in</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewSalesRepDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateSalesRep} data-testid="add-sales-rep-btn">
                            Add Sales Rep
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Sales Rep Dialog */}
            <Dialog open={assignRepDialog.open} onOpenChange={(open) => setAssignRepDialog({ ...assignRepDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Assign Sales Rep</DialogTitle>
                        <DialogDescription>
                            Assign a sales representative to {assignRepDialog.advertiser?.business_name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Select Sales Rep</Label>
                            <div className="space-y-2 mt-2">
                                <Button 
                                    variant="outline" 
                                    className="w-full justify-start"
                                    onClick={() => handleAssignSalesRep(null)}
                                >
                                    <span className="text-gray-400">Unassigned (no rep)</span>
                                </Button>
                                {salesReps.filter(r => r.is_active).map(rep => (
                                    <Button 
                                        key={rep.id}
                                        variant={assignRepDialog.advertiser?.sales_rep_id === rep.id ? 'default' : 'outline'}
                                        className="w-full justify-start"
                                        onClick={() => handleAssignSalesRep(rep.id)}
                                    >
                                        <UserCheck className="w-4 h-4 mr-2" />
                                        {rep.name} ({rep.commission_rate}% commission)
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAssignRepDialog({ open: false, advertiser: null })}>
                            Cancel
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Category Dialog */}
            <Dialog open={newCategoryDialog} onOpenChange={setNewCategoryDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add New Category</DialogTitle>
                        <DialogDescription>
                            Create a new listing category with a custom color
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Category Name *</Label>
                            <Input 
                                value={newCategory.name} 
                                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} 
                                placeholder="e.g., Hotel Technology"
                                data-testid="new-category-name-input"
                            />
                        </div>
                        <div>
                            <Label>Background Color</Label>
                            <Select value={newCategory.color_name} onValueChange={(v) => setNewCategory({ ...newCategory, color_name: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categoryColors.map(color => (
                                        <SelectItem key={color.name} value={color.name}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-4 h-4 rounded ${color.color.split(' ')[0]}`} />
                                                {color.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Preview */}
                        <div>
                            <Label>Preview</Label>
                            <div className={`rounded-xl p-4 border-2 mt-2 ${categoryColors.find(c => c.name === newCategory.color_name)?.color || 'bg-slate-100 border-slate-300'}`}>
                                <h3 className="font-semibold text-gray-800">{newCategory.name || 'Category Name'}</h3>
                                <p className="text-sm text-gray-600">0 listings</p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewCategoryDialog(false)}>Cancel</Button>
                        <Button onClick={handleCreateCategory} data-testid="create-category-btn">
                            Create Category
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Category Dialog */}
            <Dialog open={editCategoryDialog.open} onOpenChange={(open) => setEditCategoryDialog({ ...editCategoryDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Category</DialogTitle>
                        <DialogDescription>
                            Update the category name or color
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Category Name</Label>
                            <Input 
                                value={editCategory.name} 
                                onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })} 
                                placeholder="Category name"
                            />
                        </div>
                        <div>
                            <Label>Background Color</Label>
                            <Select value={editCategory.color_name} onValueChange={(v) => setEditCategory({ ...editCategory, color_name: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categoryColors.map(color => (
                                        <SelectItem key={color.name} value={color.name}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-4 h-4 rounded ${color.color.split(' ')[0]}`} />
                                                {color.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Preview */}
                        <div>
                            <Label>Preview</Label>
                            <div className={`rounded-xl p-4 border-2 mt-2 ${categoryColors.find(c => c.name === editCategory.color_name)?.color || 'bg-slate-100 border-slate-300'}`}>
                                <h3 className="font-semibold text-gray-800">{editCategory.name || 'Category Name'}</h3>
                                <p className="text-sm text-gray-600">{editCategoryDialog.category?.subcategories?.length || 0} subcategories</p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditCategoryDialog({ open: false, category: null })}>Cancel</Button>
                        <Button onClick={handleUpdateCategory}>
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* New Subcategory Dialog */}
            <Dialog open={newSubcatDialog.open} onOpenChange={(open) => setNewSubcatDialog({ ...newSubcatDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Subcategory</DialogTitle>
                        <DialogDescription>
                            Add a new subcategory to {newSubcatDialog.category?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Subcategory Name *</Label>
                            <Input 
                                value={newSubcat.name} 
                                onChange={(e) => setNewSubcat({ name: e.target.value })} 
                                placeholder="e.g., Front Desk, Linen, etc."
                                data-testid="new-subcat-name-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNewSubcatDialog({ open: false, category: null })}>Cancel</Button>
                        <Button onClick={handleAddSubcategory} data-testid="create-subcat-btn">
                            Add Subcategory
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Subcategory Dialog */}
            <Dialog open={editSubcatDialog.open} onOpenChange={(open) => setEditSubcatDialog({ ...editSubcatDialog, open })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Subcategory</DialogTitle>
                        <DialogDescription>
                            Update the subcategory name
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Subcategory Name</Label>
                            <Input 
                                value={editSubcat.name} 
                                onChange={(e) => setEditSubcat({ name: e.target.value })} 
                                placeholder="Subcategory name"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditSubcatDialog({ open: false, category: null, subcat: null })}>Cancel</Button>
                        <Button onClick={handleUpdateSubcategory}>
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDashboardPage;
